import React, { useState } from "react";
import {
  Badge,
  Button,
  Icon,
  IconButton,
  styles as ui,
  useConfirm,
} from "stories/Admin";
import { addMinutesToTime } from "../jamatPeriodUtils";
import SlidersIcon from "../SlidersIcon";
import OffsetModal from "./OffsetModal/OffsetModal";
import styles from "./PrayerTimeRow.module.scss";

interface PrayerTimes {
  id?: number;
  fajr: string;
  duhr: string;
  asr: string;
  maghrib: string;
  isha: string;
  fajrJamatOffset?: number;
  duhrJamatOffset?: number;
  asrJamatOffset?: number;
  maghribJamatOffset?: number;
  ishaJamatOffset?: number;
}

type Prayer = "fajr" | "duhr" | "asr" | "maghrib" | "isha";

const PRAYERS: Array<{ key: Prayer; label: string }> = [
  { key: "fajr", label: "Fajr" },
  { key: "duhr", label: "Dhuhr" },
  { key: "asr", label: "Asr" },
  { key: "maghrib", label: "Maghrib" },
  { key: "isha", label: "Isha" },
];

interface PrayerTimeRowProps {
  dateKey: string;
  times: PrayerTimes;
  formattedDate: string;
  /** Short date for the table, e.g. «01. okt». */
  shortDate?: string;
  isFriday?: boolean;
  previousTimes?: PrayerTimes | null;
  /** Bønnetiden offseten regnes fra, når den er kjent (fra backend). */
  salahBase?: Partial<Record<Prayer, string>>;
  onPrayerTimeChange: (
    dateKey: string,
    prayer: "fajr" | "duhr" | "asr" | "maghrib" | "isha",
    time: string,
  ) => void;
  onOffsetChange: (
    dateKey: string,
    offset:
      | "fajrJamatOffset"
      | "duhrJamatOffset"
      | "asrJamatOffset"
      | "maghribJamatOffset"
      | "ishaJamatOffset",
    value: number,
  ) => void;
  onSave: (dateKey: string) => void;
  onCreate: (dateKey: string, times: PrayerTimes) => void;
  onDelete: (dateKey: string) => void;
  fetchLatestPrayerTimes: () => void;
}

const PrayerTimeRow: React.FC<PrayerTimeRowProps> = ({
  dateKey,
  times,
  formattedDate,
  shortDate,
  isFriday,
  previousTimes,
  salahBase,
  onPrayerTimeChange,
  onOffsetChange,
  onSave,
  onCreate,
  onDelete,
  fetchLatestPrayerTimes,
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isFormActive, setFormActive] = useState(false);
  const confirm = useConfirm();

  const hasValidTimes = () => {
    return (
      (times.fajr !== "" && times.fajr !== "00:00") ||
      (times.duhr !== "" && times.duhr !== "00:00") ||
      (times.asr !== "" && times.asr !== "00:00") ||
      (times.maghrib !== "" && times.maghrib !== "00:00") ||
      (times.isha !== "" && times.isha !== "00:00")
    );
  };

  const handleActivateForm = () => {
    setFormActive(true);
  };

  const handleSave = () => {
    if (times.id !== undefined) {
      onSave(dateKey);
    } else {
      onCreate(dateKey, times);
    }
  };

  const handleDelete = async () => {
    if (times.id !== undefined) {
      const ok = await confirm({
        title: "Slette jamat-tidene?",
        message: `Jamat-tidene for ${formattedDate} slettes.`,
        confirmLabel: "Slett",
        tone: "danger",
      });
      if (!ok) return;
      onDelete(dateKey);
    } else {
      console.error("Cannot delete prayer time because id is missing.");
    }
  };

  const handleOffsetClick = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleCopyFromAbove = () => {
    if (!previousTimes) {
      console.warn("Ingen tidligere tider tilgjengelig for kopiering.");
      return;
    }

    const prayers: Array<"fajr" | "duhr" | "asr" | "maghrib" | "isha"> = [
      "fajr",
      "duhr",
      "asr",
      "maghrib",
      "isha",
    ];
    prayers.forEach((prayer) => {
      const time = previousTimes[prayer];
      onPrayerTimeChange(
        dateKey,
        prayer,
        time && time !== "00:00" ? time : "00:00",
      );
    });

    const offsets: Array<
      | "fajrJamatOffset"
      | "duhrJamatOffset"
      | "asrJamatOffset"
      | "maghribJamatOffset"
      | "ishaJamatOffset"
    > = [
      "fajrJamatOffset",
      "duhrJamatOffset",
      "asrJamatOffset",
      "maghribJamatOffset",
      "ishaJamatOffset",
    ];
    offsets.forEach((offset) => {
      const offsetValue = previousTimes[offset];
      onOffsetChange(dateKey, offset, offsetValue != null ? offsetValue : -1);
    });
  };

  const isInputDisabled = (time: string, offset?: number) => {
    return (
      time !== "" && time !== "00:00" && offset !== undefined && offset !== -1
    );
  };

  const handleTimeChange = (
    prayer: "fajr" | "duhr" | "asr" | "maghrib" | "isha",
    value: string,
  ) => {
    const cleanedValue = value.replace(/[^\d:]/g, "");

    if (cleanedValue.length === 2 && !cleanedValue.includes(":")) {
      onPrayerTimeChange(dateKey, prayer, cleanedValue + ":");
    } else if (/^(\d{0,2}):?(\d{0,2})$/.test(cleanedValue)) {
      onPrayerTimeChange(dateKey, prayer, cleanedValue);
    }
  };

  const handleTimeBlur = (
    prayer: "fajr" | "duhr" | "asr" | "maghrib" | "isha",
    value: string,
  ) => {
    if (value === "") {
      onPrayerTimeChange(dateKey, prayer, "");
    } else if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(value)) {
      onPrayerTimeChange(dateKey, prayer, "00:00");
    }
  };

  const isFirstDayOfMonth = dateKey.startsWith("01");
  const showForm = isFormActive || hasValidTimes();

  const renderTimeCell = ({ key, label }: { key: Prayer; label: string }) => {
    const time = times[key];
    const offset = times[`${key}JamatOffset` as keyof PrayerTimes] as
      | number
      | undefined;
    const disabled = isInputDisabled(time, offset);
    const base = salahBase?.[key];
    const shown =
      disabled && base && offset !== undefined
        ? addMinutesToTime(base, offset)
        : null;
    return (
      <td key={key}>
        {disabled ? (
          <span
            className={`${ui.inp} ${styles.time} ${styles.offset}`}
            aria-label={`${label} ${formattedDate}: ${offset} minutter etter bønnetiden${shown ? `, ${shown}` : ""}`}
            title={base ? `Bønnetid ${base} + ${offset} min` : undefined}
          >
            {shown ? (
              <>
                <span className={`${ui.num} ${styles.computed}`}>{shown}</span>
                <span className={styles.offsetTag}>+{offset}</span>
              </>
            ) : (
              <>+{offset} min</>
            )}
          </span>
        ) : (
          <input
            type="text"
            inputMode="numeric"
            className={`${ui.inp} ${ui.num} ${styles.time}`}
            value={time === "00:00" || time === "" ? "" : time}
            onChange={(e) => handleTimeChange(key, e.target.value)}
            onBlur={(e) => handleTimeBlur(key, e.target.value)}
            placeholder="tt:mm"
            aria-label={`${label} ${formattedDate}`}
          />
        )}
      </td>
    );
  };

  return (
    <tr>
      <td className={styles.dateCell}>
        <b className={ui.num}>{shortDate ?? formattedDate}</b>
        {isFriday && (
          <Badge tone="gold" className={styles.friday}>
            Fredag
          </Badge>
        )}
      </td>
      {showForm ? (
        <>
          {PRAYERS.map(renderTimeCell)}
          <td>
            <span className={styles.actions}>
              <IconButton
                label="Bruk offset"
                icon={<SlidersIcon />}
                onClick={handleOffsetClick}
              />
              {!isFirstDayOfMonth && (
                <IconButton
                  label="Kopier fra forrige dag"
                  icon={<Icon name="copy" size={16} strokeWidth={2} />}
                  onClick={handleCopyFromAbove}
                  disabled={!previousTimes}
                />
              )}
              <IconButton
                label="Slett"
                icon={<Icon name="trash" size={16} strokeWidth={2} />}
                onClick={handleDelete}
                disabled={times.id === 0}
              />
            </span>
            <OffsetModal
              show={isModalOpen}
              formattedDate={formattedDate}
              times={times}
              dateKey={dateKey}
              onInputChange={onOffsetChange}
              onSave={onSave}
              onClose={closeModal}
              fetchLatestPrayerTimes={fetchLatestPrayerTimes}
            />
          </td>
        </>
      ) : (
        <>
          <td colSpan={5}>
            <span className={styles.none}>Ingen jamat-tider</span>
          </td>
          <td>
            <span className={styles.actions}>
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon name="plus" size={14} strokeWidth={2.2} />}
                onClick={handleActivateForm}
                aria-label={`Legg til jamat-tider for ${formattedDate}`}
              >
                Legg til
              </Button>
            </span>
          </td>
        </>
      )}
    </tr>
  );
};

export default PrayerTimeRow;
